import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../../stores'
import {
  analyticsService,
  formatWatchTime,
  type AnalyticsOverview,
  type VideoPerformance,
  type ViewsOverTime,
} from '../../services'
import {
  Button,
  LoadingScreen,
  SimpleLineChart,
} from '../../components/common'

type TimeRange = '7d' | '30d' | '90d'

export function AnalyticsPage() {
  const { profile } = useAuthStore()
  const [isLoading, setIsLoading] = useState(true)
  const [timeRange, setTimeRange] = useState<TimeRange>('30d')
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null)
  const [topVideos, setTopVideos] = useState<VideoPerformance[]>([])
  const [viewsData, setViewsData] = useState<ViewsOverTime[]>([])

  const hasPaidTier = profile?.subscription_tier && profile.subscription_tier !== 'free'

  useEffect(() => {
    const loadAnalytics = async () => {
      if (!profile || !hasPaidTier) {
        setIsLoading(false)
        return
      }

      try {
        const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90

        const [overviewData, videosData, viewsOverTime] = await Promise.all([
          analyticsService.getOverview(profile.id),
          analyticsService.getTopVideos(profile.id, 5),
          analyticsService.getViewsOverTime(profile.id, days),
        ])

        setOverview(overviewData)
        setTopVideos(videosData)
        setViewsData(viewsOverTime)
      } catch (error) {
        console.error('Failed to load analytics:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadAnalytics()
  }, [profile, hasPaidTier, timeRange])

  if (!hasPaidTier) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <svg
          className="w-16 h-16 text-gray-400 mx-auto mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Analytics Locked
        </h1>
        <p className="text-gray-600 mb-6">
          Upgrade to Basic or higher to access analytics and track your video performance.
        </p>
        <Link to="/dashboard/subscription">
          <Button>Upgrade Subscription</Button>
        </Link>
      </div>
    )
  }

  if (isLoading) {
    return <LoadingScreen message="Loading analytics..." />
  }

  // Format views data for chart
  const chartData = viewsData.map(d => ({
    label: formatDate(d.date, timeRange),
    value: d.views,
  }))

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="mt-1 text-gray-600">
            Track your video performance and client engagement.
          </p>
        </div>

        {/* Time range selector */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Period:</span>
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            {(['7d', '30d', '90d'] as TimeRange[]).map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  timeRange === range
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : '90 Days'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <StatCard
          label="Total Views"
          value={overview?.totalViews || 0}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          }
          color="blue"
        />
        <StatCard
          label="Watch Time"
          value={formatWatchTime(overview?.totalWatchTime || 0)}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
          color="green"
          isText
        />
        <StatCard
          label="Completion Rate"
          value={`${overview?.completionRate || 0}%`}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
          color="purple"
          isText
        />
        <StatCard
          label="Active Clients"
          value={overview?.activeClients || 0}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          }
          color="orange"
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total Videos</p>
          <p className="text-2xl font-bold text-gray-900">{overview?.totalVideos || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total Likes</p>
          <p className="text-2xl font-bold text-gray-900">{overview?.totalLikes || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Comments</p>
          <p className="text-2xl font-bold text-gray-900">{overview?.totalComments || 0}</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Views Over Time */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Views Over Time</h3>
          {chartData.length > 0 && chartData.some(d => d.value > 0) ? (
            <SimpleLineChart
              data={chartData}
              height={240}
              lineColor="stroke-blue-500"
              fillColor="fill-blue-100"
            />
          ) : (
            <div className="h-60 bg-gray-50 rounded-lg flex flex-col items-center justify-center">
              <svg className="w-12 h-12 text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <p className="text-gray-400">No views yet</p>
              <p className="text-sm text-gray-400">Views will appear here once your videos are watched</p>
            </div>
          )}
        </div>

        {/* Top Videos */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Top Videos</h3>
          {topVideos.length > 0 ? (
            <div className="space-y-3">
              {topVideos.map((video, index) => (
                <Link
                  key={video.id}
                  to={`/dashboard/videos/${video.id}/edit`}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <span className="w-6 h-6 flex items-center justify-center bg-gray-100 rounded-full text-sm font-medium text-gray-600">
                    {index + 1}
                  </span>
                  <div className="w-16 h-10 bg-gray-200 rounded overflow-hidden flex-shrink-0">
                    {video.thumbnail_url ? (
                      <img
                        src={video.thumbnail_url}
                        alt={video.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{video.title}</p>
                    <p className="text-xs text-gray-500">
                      {video.view_count} views
                    </p>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                      {video.like_count}
                    </span>
                    <span className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      {video.comment_count}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="h-60 bg-gray-50 rounded-lg flex flex-col items-center justify-center">
              <svg className="w-12 h-12 text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <p className="text-gray-400">No videos yet</p>
              <Link to="/dashboard/videos/upload" className="text-sm text-blue-600 hover:underline mt-1">
                Upload your first video
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Engagement breakdown */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Engagement Breakdown</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <EngagementItem
            label="Avg. Views/Video"
            value={overview?.totalVideos ? Math.round(overview.totalViews / overview.totalVideos) : 0}
          />
          <EngagementItem
            label="Avg. Likes/Video"
            value={overview?.totalVideos ? Math.round(overview.totalLikes / overview.totalVideos) : 0}
          />
          <EngagementItem
            label="Avg. Comments/Video"
            value={overview?.totalVideos ? (overview.totalComments / overview.totalVideos).toFixed(1) : '0'}
          />
          <EngagementItem
            label="Avg. Watch Time"
            value={
              overview?.totalViews
                ? formatWatchTime(Math.round(overview.totalWatchTime / overview.totalViews))
                : '0s'
            }
          />
        </div>
      </div>
    </div>
  )
}

interface StatCardProps {
  label: string
  value: number | string
  icon: React.ReactNode
  color: 'blue' | 'green' | 'purple' | 'orange'
  isText?: boolean
}

function StatCard({ label, value, icon, color, isText }: StatCardProps) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 lg:p-6">
      <div className="flex items-center gap-3 lg:gap-4">
        <div className={`p-2 lg:p-3 rounded-lg ${colorClasses[color]}`}>{icon}</div>
        <div>
          <p className="text-xl lg:text-2xl font-bold text-gray-900">
            {isText ? value : typeof value === 'number' ? value.toLocaleString() : value}
          </p>
          <p className="text-xs lg:text-sm text-gray-600">{label}</p>
        </div>
      </div>
    </div>
  )
}

interface EngagementItemProps {
  label: string
  value: number | string
}

function EngagementItem({ label, value }: EngagementItemProps) {
  return (
    <div className="text-center p-4 bg-gray-50 rounded-lg">
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-600">{label}</p>
    </div>
  )
}

function formatDate(dateStr: string, range: TimeRange): string {
  const date = new Date(dateStr)
  if (range === '7d') {
    return date.toLocaleDateString('en-US', { weekday: 'short' })
  }
  if (range === '30d') {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
